/**
 * SSE 프레임 파서 — 구 Nitro `parseBackendSse` 의 이관분(F-0 §1.1 L1).
 *
 * 백엔드 프레임: `event: <type>\ndata: <json>\n\n` (`ensure_ascii=False`).
 *
 * **왜 라이브러리를 쓰지 않나**: `EventSource` 는 POST 바디를 못 싣는다(채팅은 POST 다).
 * 그래서 fetch 스트림을 직접 읽는데, 그때 반드시 지켜야 하는 것이 **청크 경계**다 — 네트워크
 * 청크는 프레임 중간에서 끊긴다. 버퍼링 없이 조각을 파싱하면 한글이 깨지거나 JSON 이 잘린다.
 * 구 구현이 `TextDecoder({stream:true})` + 구분자 버퍼링을 쓴 이유가 그것이고, 여기서도 같다.
 */
import type { RunEvent } from './events'

/** 한 프레임 텍스트 → 이벤트. data 가 없으면 null(주석·빈 프레임). */
export function parseFrame(frame: string): RunEvent | null {
  let type = 'message'
  const dataLines: string[] = []
  for (const raw of frame.split('\n')) {
    const line = raw.replace(/\r$/, '')
    if (line.startsWith('event:')) type = line.slice(6).trim()
    else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim())
  }
  if (dataLines.length === 0) return null
  const payload = dataLines.join('\n')
  try {
    return { type, data: JSON.parse(payload) as Record<string, unknown> }
  } catch {
    // JSON 이 아니어도 버리지 않는다 — 무엇이 왔는지는 화면·로그가 말할 수 있어야 한다.
    return { type, data: { raw: payload } }
  }
}

/** 바이트 스트림 → 이벤트 스트림. 프레임 구분자(`\n\n`·`\r\n\r\n`) 단위로 끊는다. */
export async function* parseSseStream(
  body: ReadableStream<Uint8Array>,
): AsyncGenerator<RunEvent> {
  const reader = body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  try {
    for (;;) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true }) // stream:true — 멀티바이트 문자 보존
      for (;;) {
        const separator = buffer.search(/\r?\n\r?\n/)
        if (separator === -1) break
        const matched = /^\r?\n\r?\n/.exec(buffer.slice(separator))
        const frame = buffer.slice(0, separator)
        buffer = buffer.slice(separator + (matched ? matched[0].length : 2))
        const event = parseFrame(frame)
        if (event) yield event
      }
    }
    // 마지막 프레임이 구분자 없이 끝나는 경우(서버 종료 타이밍) — 버리지 않는다.
    const tail = parseFrame(buffer)
    if (tail) yield tail
  } finally {
    reader.releaseLock()
  }
}
